import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Chip,
  Typography,
  CircularProgress,
  TextField,
  Box,
  Button,
  InputAdornment,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import MedicationIcon from '@mui/icons-material/Medication';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router';
import { useUser } from '@clerk/react';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';

interface HistoryItem {
  patientId?: string;
  consultationToken?: string | null;
  date: string;
  name?: string;
  age?: number;
  gender?: string;
  description: string;
  remedy_validated?: string | null;
  remedy_full_name?: string | null;
  remedy_potency?: string | null;
  remedy_2_short_name?: string | null;
  remedy_2_full_name?: string | null;
  remedy_2_potency?: string | null;
  remedy_2_available_at?: string | null;
  remedy_2_is_available?: boolean;
  remedy_2_requires_feedback?: boolean;
  primary_remedy_feedback?: string | null;
  primary_remedy_feedback_date?: string | null;
  tailored_description_2?: string | null;
  administration_instructions_2?: string | null;
  validated?: boolean;
  validated_at?: string | null;
  steps: number[];
}

const STORAGE_KEY_PATIENT = 'homeopathy_patient';

export function History() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { patient, setPatient, setToast } = useGlobalContext();
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters state
  const [nameFilter, setNameFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Feedback input state per consultation
  const [feedbackInputs, setFeedbackInputs] = useState<Record<string, string>>({});
  const [submittingFeedbackKey, setSubmittingFeedbackKey] = useState<string | null>(null);

  const accountEmail = user?.primaryEmailAddress?.emailAddress || patient.email;

  const fetchHistory = useCallback(async () => {
    if (!accountEmail) return;
    setLoading(true);
    try {
      const res = await apiClient.get('/patients/history', {
        params: { email: accountEmail },
      });
      setHistory(res.data.history || []);
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error(error);
      setToast({ type: 'error', message: 'Error loading history.' });
    } finally {
      setLoading(false);
    }
  }, [accountEmail, setToast]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFeedbackSubmit = async (item: HistoryItem) => {
    const key = item.consultationToken || item.date;
    const text = (feedbackInputs[key] || '').trim();
    if (!text) return;

    setSubmittingFeedbackKey(key);
    try {
      await apiClient.post('/patients/feedback', {
        email: accountEmail,
        date_0: item.date,
        consultation_token: item.consultationToken,
        feedback: text,
        name: item.name,
      });
      setToast({ type: 'success', message: 'Feedback submitted! 2nd remedy unlocked.' });
      await fetchHistory();
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(err);
      setToast({ type: 'error', message: 'Failed to save feedback.' });
    } finally {
      setSubmittingFeedbackKey(null);
    }
  };

  // Filtered history calculation
  const filteredHistory = useMemo(() => {
    const list = history.filter((item) => {
      // 1. Patient Name Filter (case-insensitive substring match)
      if (nameFilter.trim()) {
        const query = nameFilter.trim().toLowerCase();
        const itemName = (item.name || '').toLowerCase();
        if (!itemName.includes(query)) return false;
      }

      // 2. Date Range Filter (startDate & endDate YYYY-MM-DD)
      const itemDateOnly = item.date ? item.date.slice(0, 10) : '';
      if (startDate && itemDateOnly < startDate) {
        return false;
      }
      if (endDate && itemDateOnly > endDate) {
        return false;
      }

      return true;
    });

    return [...list].sort((a, b) => {
      const keyA = a.validated_at || a.date || '';
      const keyB = b.validated_at || b.date || '';
      return keyB.localeCompare(keyA);
    });
  }, [history, nameFilter, startDate, endDate]);

  const hasActiveFilters = Boolean(nameFilter || startDate || endDate);

  const handleClearFilters = () => {
    setNameFilter('');
    setStartDate('');
    setEndDate('');
  };

  const handleResumeConsultation = (item: HistoryItem) => {
    if (!item.consultationToken) {
      setToast({ type: 'error', message: 'This consultation cannot be resumed.' });
      return;
    }
    const ageNum = item.age ?? patient.age ?? 18;
    const updatedPatient = {
      email: accountEmail,
      name: item.name || patient.name,
      age: ageNum,
      gender: item.gender || patient.gender,
      consultationToken: item.consultationToken,
    };
    setPatient(updatedPatient);
    try {
      localStorage.setItem(STORAGE_KEY_PATIENT, JSON.stringify(updatedPatient));
    } catch (_) {}

    // Determine target step to resume to
    let targetPath = '/description';
    if (item.steps.includes(5)) {
      targetPath = '/results';
    } else if (item.steps.includes(4)) {
      targetPath = '/intake';
    } else if (item.steps.includes(3)) {
      targetPath = '/mind';
    } else if (item.steps.includes(0) || item.steps.includes(1) || item.steps.includes(2)) {
      targetPath = '/physical-symptom';
    }

    setToast({ type: 'success', message: 'Consultation loaded to resume.' });
    navigate(targetPath);
  };

  const handleViewValidatedConsultation = (item: HistoryItem) => {
    if (!item.consultationToken) return;
    let ageNum = patient.age;
    if (typeof item.age === 'number') {
      ageNum = item.age;
    }
    const updatedPatient = {
      email: accountEmail,
      name: item.name || patient.name,
      age: ageNum,
      gender: item.gender || patient.gender,
      consultationToken: item.consultationToken,
    };
    setPatient(updatedPatient);
    try {
      localStorage.setItem(STORAGE_KEY_PATIENT, JSON.stringify(updatedPatient));
    } catch (_) {}

    setToast({ type: 'success', message: 'Opening validated consultation (Read-Only)...' });
    navigate('/results');
  };

  if (!accountEmail) {
    return (
      <div className="p-6">
        <Typography variant="h6" className="text-gray-800">
          Please register or log in under "Personal Data" to view your history.
        </Typography>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Typography variant="h4" className="font-bold text-slate-800">
            Consultation History
          </Typography>
          <Typography variant="body2" className="text-slate-500 mt-1">
            Search and manage past homeopathic consultations for your account.
          </Typography>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <Paper elevation={0} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm mb-2">
          <CalendarMonthIcon fontSize="small" className="text-blue-600" />
          <span>Filters & Search</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <TextField
            fullWidth
            size="small"
            label="Patient Name"
            placeholder="Search by name..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" className="text-slate-400" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label="From Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label="To Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        {hasActiveFilters && (
          <div className="flex justify-between items-center pt-2 border-t border-slate-200">
            <Typography variant="caption" className="text-slate-500 font-medium">
              Showing {filteredHistory.length} of {history.length} consultations
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<FilterAltOffIcon />}
              onClick={handleClearFilters}
              className="normal-case text-xs"
            >
              Clear filters
            </Button>
          </div>
        )}
      </Paper>

      {loading && (
        <Box className="flex justify-center p-8">
          <CircularProgress />
        </Box>
      )}

      {!loading && history.length === 0 && (
        <Typography className="text-slate-600 p-4">
          No consultations registered for {patient.name || accountEmail}.
        </Typography>
      )}

      {!loading && history.length > 0 && filteredHistory.length === 0 && (
        <Paper elevation={0} className="p-6 text-center border border-dashed border-slate-300 rounded-xl">
          <Typography variant="body1" className="text-slate-600">
            No consultations match your filter criteria.
          </Typography>
          <Button size="small" variant="text" onClick={handleClearFilters} className="mt-2 text-blue-600 font-medium">
            Clear filters
          </Button>
        </Paper>
      )}

      <div className="grid gap-4">
        {filteredHistory.map((item, idx) => {
          const patientName = item.name || patient.name;
          const patientAge = item.age ?? patient.age;
          const rawGender = item.gender || patient.gender;
          const formattedGender = rawGender
            ? rawGender.charAt(0).toUpperCase() + rawGender.slice(1).toLowerCase()
            : '';

          const parts: string[] = [];
          if (patientName) parts.push(`Patient: ${patientName}`);
          if (patientAge !== undefined && patientAge !== null) parts.push(`Age: ${patientAge}`);
          if (formattedGender) parts.push(`Gender: ${formattedGender}`);

          return (
            <Card key={`${item.date}-${idx}`} className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="space-y-3">
                <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Typography variant="h6" className="font-semibold text-blue-900">
                        Consultation on {item.date}
                      </Typography>
                      {item.validated ? (
                        <Chip
                          size="small"
                          icon={<CheckCircleIcon />}
                          label="Validated"
                          color="success"
                          variant="outlined"
                          className="font-medium text-xs"
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<HourglassEmptyIcon />}
                          label="In Progress"
                          color="warning"
                          variant="outlined"
                          className="font-medium text-xs"
                        />
                      )}
                    </div>

                    {parts.length > 0 && (
                      <Typography variant="body2" className="text-xs text-slate-600 font-medium">
                        {parts.join(' • ')}
                      </Typography>
                    )}
                  </div>

                  {/* Prescribed Remedies Display (1st and 2nd Remedies) */}
                  <div className="flex flex-col gap-2 min-w-[280px]">
                    {item.validated && item.remedy_validated ? (
                      <>
                        {/* Primary Remedy Card */}
                        <div className="rounded-lg p-2.5 bg-emerald-50 border border-emerald-200 flex items-start gap-2">
                          <MedicationIcon className="text-emerald-600 text-xl mt-0.5" />
                          <div className="flex-1">
                            <div className="flex justify-between items-center gap-1 flex-wrap">
                              <Typography variant="caption" className="block text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                                1st Prescribed Remedy (Primary)
                              </Typography>
                              {item.validated_at && (
                                <Typography variant="caption" className="text-[10px] text-emerald-700 font-medium">
                                  Validated: {item.validated_at.slice(0, 10)}
                                </Typography>
                              )}
                            </div>
                            <Typography variant="body2" className="font-bold leading-tight text-emerald-900 mt-0.5">
                              {item.remedy_full_name && item.remedy_full_name !== item.remedy_validated
                                ? `${item.remedy_validated} — ${item.remedy_full_name} (${item.remedy_potency || '30CH'})`
                                : `${item.remedy_validated} (${item.remedy_potency || '30CH'})`}
                            </Typography>
                          </div>
                        </div>

                        {/* Secondary Remedy Card */}
                        <div
                          className={`rounded-lg p-2.5 border flex items-start gap-2 ${
                            item.remedy_2_is_available
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-amber-50 border-amber-200'
                          }`}
                        >
                          <MedicationIcon
                            className={item.remedy_2_is_available ? 'text-blue-600 text-xl mt-0.5' : 'text-amber-600 text-xl mt-0.5'}
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-center gap-1 flex-wrap">
                              <Typography
                                variant="caption"
                                className={`block text-[10px] font-bold uppercase tracking-wide ${
                                  item.remedy_2_is_available ? 'text-blue-800' : 'text-amber-800'
                                }`}
                              >
                                2nd Prescribed Remedy (Secondary)
                              </Typography>
                              {item.remedy_2_available_at && (
                                <Typography
                                  variant="caption"
                                  className={`text-[10px] font-semibold ${
                                    item.remedy_2_is_available ? 'text-blue-700' : 'text-amber-700'
                                  }`}
                                >
                                  {item.remedy_2_is_available
                                    ? `Available: ${item.remedy_2_available_at.slice(0, 10)}`
                                    : `${(() => {
                                        const availDate = new Date(item.remedy_2_available_at.replace(' ', 'T'));
                                        const diff = Math.max(0, Math.ceil((availDate.getTime() - new Date().getTime()) / 86400000));
                                        return `To be released in ${diff} ${diff === 1 ? 'day' : 'days'}`;
                                      })()}`}
                                </Typography>
                              )}
                            </div>
                            {item.remedy_2_is_available ? (
                              <>
                                <Typography variant="body2" className="font-bold leading-tight text-blue-900 mt-0.5">
                                  {item.remedy_2_short_name
                                    ? item.remedy_2_full_name && item.remedy_2_full_name !== item.remedy_2_short_name
                                      ? `${item.remedy_2_short_name} — ${item.remedy_2_full_name} (${item.remedy_2_potency || '30CH'})`
                                      : `${item.remedy_2_short_name} (${item.remedy_2_potency || '30CH'})`
                                    : 'Available'}
                                </Typography>
                                {item.primary_remedy_feedback && (
                                  <div className="bg-white/90 border border-blue-200 rounded p-1.5 text-[11px] text-blue-900 mt-1.5">
                                    <span className="font-semibold block text-[10px] text-blue-800">
                                      💬 Evolution Feedback ({item.primary_remedy_feedback_date?.slice(0, 10)}):
                                    </span>
                                    <span className="italic">"{item.primary_remedy_feedback}"</span>
                                  </div>
                                )}
                              </>
                            ) : item.remedy_2_requires_feedback ? (
                              <div className="mt-1 space-y-1.5 bg-amber-100/70 p-2 rounded border border-amber-300">
                                <Typography variant="caption" className="block text-[11px] font-bold text-amber-900 leading-tight">
                                  💬 Evolution Feedback Required
                                </Typography>
                                <Typography variant="caption" className="block text-[10px] text-amber-800 leading-tight">
                                  Please provide feedback on how the patient evolved after taking the 1st remedy to unlock the 2nd prescribed remedy:
                                </Typography>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  size="small"
                                  placeholder="Describe patient's progress, changes or evolution..."
                                  value={feedbackInputs[item.consultationToken || item.date] || ''}
                                  onChange={(e) => {
                                    const key = item.consultationToken || item.date;
                                    setFeedbackInputs((prev) => ({ ...prev, [key]: e.target.value }));
                                  }}
                                  className="bg-white rounded text-xs"
                                  InputProps={{ style: { fontSize: '11px' } }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  disabled={
                                    submittingFeedbackKey === (item.consultationToken || item.date) ||
                                    !(feedbackInputs[item.consultationToken || item.date] || '').trim()
                                  }
                                  onClick={() => handleFeedbackSubmit(item)}
                                  className="normal-case text-[10px] font-bold py-0.5 px-2"
                                >
                                  {submittingFeedbackKey === (item.consultationToken || item.date)
                                    ? 'Submitting...'
                                    : 'Submit Feedback & Unlock 2nd Remedy'}
                                </Button>
                              </div>
                            ) : (
                              <Typography variant="body2" className="font-semibold text-xs text-amber-900 mt-0.5">
                                🔒 {(() => {
                                  const availDate = new Date(item.remedy_2_available_at ? item.remedy_2_available_at.replace(' ', 'T') : new Date());
                                  const diff = Math.max(0, Math.ceil((availDate.getTime() - new Date().getTime()) / 86400000));
                                  return `To be released in ${diff} ${diff === 1 ? 'day' : 'days'}`;
                                })()} (Available on {item.remedy_2_available_at ? item.remedy_2_available_at.slice(0, 10) : 'date'})
                              </Typography>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-lg p-2.5 bg-amber-50 border border-amber-200 flex items-center gap-2 min-w-[220px]">
                        <MedicationIcon className="text-amber-600 text-xl" />
                        <div>
                          <Typography variant="caption" className="block text-[10px] font-bold uppercase tracking-wide text-amber-800">
                            Offered Remedy
                          </Typography>
                          <Typography variant="body2" className="font-bold leading-tight text-amber-900">
                            🔒 Locked (Requires validation & payment)
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Typography variant="body2" className="text-slate-700 bg-slate-50 p-3 rounded-md border border-slate-100">
                  {item.description}
                </Typography>

                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <div className="flex gap-2 flex-wrap">
                    <Chip
                      label="Description (Step 2)"
                      color={item.steps.includes(0) || item.steps.includes(1) || item.steps.includes(2) ? 'success' : 'default'}
                      variant={item.steps.includes(0) || item.steps.includes(1) || item.steps.includes(2) ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      label="Physical (Step 3)"
                      color={item.steps.includes(3) ? 'success' : 'default'}
                      variant={item.steps.includes(3) ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      label="Mental (Step 4)"
                      color={item.steps.includes(4) ? 'success' : 'default'}
                      variant={item.steps.includes(4) ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      label="Intake (Step 5)"
                      color={item.steps.includes(5) ? 'success' : 'default'}
                      variant={item.steps.includes(5) ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </div>

                  {item.validated && item.consultationToken ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewValidatedConsultation(item)}
                      className="normal-case font-semibold text-xs ml-auto"
                    >
                      View Consultation
                    </Button>
                  ) : item.consultationToken ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleResumeConsultation(item)}
                      className="normal-case font-semibold text-xs ml-auto"
                    >
                      Resume & Finish Consultation
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
